import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { api } from '../lib/api';

export type SubscriptionStatus = {
  effectivePlan: 'free' | 'pro' | 'unlimited' | 'starter' | 'professional' | 'business' | 'flat' | 'partner';
  rawPlan: string;
  planExpiresAt: string | null;
  billingInterval: 'monthly' | 'annual' | null;
  trial: { active: boolean; endsAt: string | null };
  addons: {
    startklar: boolean;
    startklarExpiresAt: string | null;
    team: boolean;
  };
  maxSeats: number;
  reportUsage:
    | { mode: 'total'; used: number; limit: number; credits: number }
    | { mode: 'monthly'; used: number; limit: number; credits: number; periodKey?: string }
    | { mode: 'unlimited'; used: number; credits: number }
    | { mode: 'pool'; used: number; available: number; tariffCredits: number; credits: number };
  features: {
    esf: boolean;
    templates: boolean;
    pdfExport: boolean;
    docxExport: boolean;
    team: boolean;
  };
};

interface SubscriptionContextType {
  status: SubscriptionStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  status: null,
  loading: true,
  error: null,
  refresh: async () => {},
});

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<SubscriptionStatus>('/api/auth/me/subscription-status');
      setStatus(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden des Subscription-Status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SubscriptionContext.Provider value={{ status, loading, error, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionStatus() {
  return useContext(SubscriptionContext);
}
